import type { InternalAxiosRequestConfig } from 'axios';

export const DOCBITS_TRACE_ID_HEADER = 'X-DocBits-Trace-Id';
export const DOCBITS_TRACE_PARENT_HEADER = 'traceparent';
export const SEARCH_TRACE_HEADER = 'X-Search-Trace-Id';
export const SEARCH_SOURCE_HEADER = 'X-Search-Source';

let lastDocbitsTraceId = '';

function randomHex(bytes: number): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    const values = new Uint8Array(bytes);
    cryptoApi.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
  }
  let out = '';
  for (let i = 0; i < bytes; i += 1) {
    out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
  }
  return out;
}

export function createDocbitsTraceId(): string {
  return randomHex(16);
}

export function createSearchTraceId(): string {
  return createDocbitsTraceId();
}

export function getLastDocbitsTraceId(): string {
  return lastDocbitsTraceId;
}

export function isDashboardSearchUrl(url?: string): boolean {
  if (!url) return false;
  return /\/document\/dashboards(?:\?|$)/.test(url);
}

function getHeader(config: InternalAxiosRequestConfig, name: string): string | undefined {
  const headers: any = config.headers;
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name) || headers.get(name.toLowerCase()) || undefined;
  return headers[name] || headers[name.toLowerCase()];
}

function setHeader(config: InternalAxiosRequestConfig, name: string, value: string): void {
  const headers: any = config.headers ?? {};
  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }
  headers[name] = value;
  config.headers = headers;
}

function traceIdFromTraceparent(traceparent?: string): string | undefined {
  if (!traceparent) return undefined;
  const parts = traceparent.trim().split('-');
  if (parts.length >= 4 && /^[0-9a-f]{32}$/i.test(parts[1])) return parts[1];
  return undefined;
}

export function attachDocbitsTraceHeaders(config: InternalAxiosRequestConfig): string {
  const existingHeaderTraceId = getHeader(config, DOCBITS_TRACE_ID_HEADER);
  const existingTraceparent = getHeader(config, DOCBITS_TRACE_PARENT_HEADER);
  const existingTraceId = traceIdFromTraceparent(existingTraceparent);
  const traceId = existingHeaderTraceId || existingTraceId || createDocbitsTraceId();
  const spanId = randomHex(8);
  if (!existingHeaderTraceId) {
    setHeader(config, DOCBITS_TRACE_ID_HEADER, traceId);
  }
  if (!existingTraceparent) {
    setHeader(config, DOCBITS_TRACE_PARENT_HEADER, `00-${traceId}-${spanId}-01`);
  }
  (config as any).__docbitsTraceId = traceId;
  lastDocbitsTraceId = traceId;
  return traceId;
}

export function attachTraceHeaders(config: InternalAxiosRequestConfig): string {
  const traceId = attachDocbitsTraceHeaders(config);
  if (isDashboardSearchUrl(config.url)) {
    // Keep the old internal field name for existing diagnostics.
    (config as any).__searchTraceId = traceId;
  }
  return traceId;
}

export function attachSearchTraceHeaders(config: InternalAxiosRequestConfig): string | undefined {
  if (!isDashboardSearchUrl(config.url)) return undefined;
  const traceId = attachDocbitsTraceHeaders(config);
  (config as any).__searchTraceId = traceId;
  return traceId;
}
